import { AnimatePresence, motion } from 'framer-motion';
import { type IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { type Campaign, type Targeting } from '@feathr/blackbox';
import { CardV2 as Card, Slider, Toggle, Tooltip } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';
import { useAccount, useUser } from '@feathr/report_components';

import useLookalikeAudience from './LookalikeAudienceTargeting.useLookalikeAudience';
import {
  getSliderMarks,
  onFormatTickLabel,
  roundValue,
  SLIDER_MAX_VALUE,
  SLIDER_MIN_VALUE,
  sliderToValue,
  valueToSlider,
} from './LookalikeAudienceTargeting.utils';

const MIN_REACHABLE_USERS = 5_000;
const MIN_REACHABLE_USERS_INTERNAL = 100;

interface IProps {
  numReachableUsers: number;
  isLoading: boolean;
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

/** Lookalike Audience Targeting for Pixl Plus campaigns */
function LookalikeAudienceTargeting({
  numReachableUsers,
  isLoading,
  campaign,
  targetings,
}: Readonly<IProps>): JSX.Element {
  const { isInternal } = useUser();
  const { t } = useTranslation();
  const { handleSliderChange, handleToggleChange, isEnabled, audienceSize } = useLookalikeAudience({
    targetings,
    campaign,
  });

  const { isPlatformLite } = useAccount();

  // Allow internal users to target with less reachable users for testing purposes
  const minReachableUsers = isInternal ? MIN_REACHABLE_USERS_INTERNAL : MIN_REACHABLE_USERS;

  const hasEnoughReachableUsers = numReachableUsers >= minReachableUsers;
  const sliderMarks = getSliderMarks(t);
  const shouldShowSlider = isEnabled && hasEnoughReachableUsers;

  const handleSliderChangeWithConversion = (sliderPosition: number): void => {
    const actualValue = sliderToValue(sliderPosition);
    const roundedValue = roundValue(actualValue);
    handleSliderChange(roundedValue);
  };

  const disabledMessage = isPlatformLite
    ? t('Lookalike targeting is only available on Essential and Advanced plans.')
    : t('Lookalike targeting requires {{minReachableUsers}} reachable people.', {
        minReachableUsers: numeral(MIN_REACHABLE_USERS).format('0,0'),
      });

  return (
    <Card disabled={isPlatformLite || !hasEnoughReachableUsers} width={'full'}>
      <Card.Header
        description={t(
          'Expand your reach by targeting people who are similar to your targeted groups.',
        )}
        title={t('Lookalike Audience Targeting')}
      >
        {!isPlatformLite && hasEnoughReachableUsers ? (
          <Toggle onChange={handleToggleChange} value={isEnabled} />
        ) : (
          <Tooltip title={disabledMessage}>{getIconForAction('locked')}</Tooltip>
        )}
      </Card.Header>

      <AnimatePresence>
        {shouldShowSlider && (
          <motion.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
          >
            <Card.Content isLoading={isLoading}>
              <Slider
                helpText={t(
                  "Use the slider below to adjust the relative size of the lookalike audience you'd like to target.",
                )}
                label={t('Lookalike Audience Size')}
                labelAlwaysOn={true}
                marks={sliderMarks}
                max={SLIDER_MAX_VALUE}
                min={SLIDER_MIN_VALUE}
                onChange={handleSliderChangeWithConversion}
                preventLabelOverflow={true}
                tickLabel={onFormatTickLabel}
                value={valueToSlider(audienceSize ?? 10_000)}
              />
            </Card.Content>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default observer(LookalikeAudienceTargeting);
